import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);

import { BaseTile } from "../enums/BaseTile";
import { Risk } from "../enums/Risk";
import * as axiosHelper from "../utils/axiosHelper";
import moment from "moment";
import { OBSERVATORY } from "../enums/Type";
import * as common from "../utils/common";
import * as s3 from "../utils/s3";

const DATE_FORMAT = "YYYY/MM/DD HH:mm";
const DOMAIN = process.env.VUE_APP_DOMAIN;

export default new Vuex.Store({
  state: {
    mobileHazardMap: {
      key: null,
      detail: []
    },
    isShowMobileHazardMap: false,
    user: {},
    baseDate: moment.utc(),
    selectedRisk: Risk.RAIN,
    selectedBaseTile: BaseTile.GRAY,
    selectedFeatures: [],
    selectedObservatory: {},
    selectedDate: moment.utc(),
    riskBaseDate: moment.utc(),
    leafletMap: null,
    isShowMapDetail: false,
    mapDetail: {
      title: "",
      contents: ""
    },
    observatories: [],
    isShowUsageGuide: false,
    imageSizeInfo: {},
    rainImageCoordinate: {},
    rainImageInfo: [],
    riskSubControl: {
      observatory: [
        OBSERVATORY.STAGE.code,
        OBSERVATORY.RAIN.code,
        OBSERVATORY.DAM.code,
        OBSERVATORY.CAMERA.code,
        OBSERVATORY.OVERFLOW_WATERWAY.code,
        OBSERVATORY.SUIJIN.code,
        OBSERVATORY.MANHOLE.code,
        OBSERVATORY.MANHOLE_WATERWAY.code,
        OBSERVATORY.PUMP.code
      ],
      opacity: 0.7
    },
    riverFloodingInfo: [],
    riverFloodingType: 3,
    dosyaInfo: {
      bounds: [],
      images: []
    },
    snowInfo: {
      bounds: [],
      images: []
    },
    usageGuide: {
      title: null,
      colors: []
    },
    hazardMapUsageGuide: {
      url: []
    },
    alerts: [],
    isNavigationVisible: false,
    isRightNavVisible: false,
    isMonitoring: true,
    isManholeMonitor: false,
    isCameraMonitor: false,
    kouzuiInfo: [],
    isLoading: false,
    wind: {
      timeSeries: [],
      data: {}
    },
    selectedWind: {
      speed: 0,
      direction: 0
    },
    isMobileObservatoryRisk: true,
    manholeChartTopImage: null,
    overflowWaterwayChartTopImage: null,
    suijinChartImage: {
      pole: null,
      carTate: null,
      carYoko: null
    }
  },
  getters: {
    mobileHazardMap: state => state.mobileHazardMap,
    baseDate: state => state.baseDate,
    selectedRisk: state => state.selectedRisk,
    selectedBaseTile: state => state.selectedBaseTile,
    selectedFeatures: state => state.selectedFeatures,
    selectedObservatory: state => state.selectedObservatory,
    selectedDate: state => state.selectedDate,
    riskBaseDate: state => state.riskBaseDate,
    imageSizeInfo: state => state.imageSizeInfo,
    rainImageInfo: state => state.rainImageInfo,
    rainImageCoordinate: state => state.rainImageCoordinate,
    leafletMap: state => state.leafletMap,
    isShowMapDetail: state => state.isShowMapDetail,
    mapDetail: state => state.mapDetail,
    observatories: state => state.observatories,
    riskSubControl: state => state.riskSubControl,
    isShowUsageGuide: state => state.isShowUsageGuide,
    riverFloodingInfo: state => state.riverFloodingInfo,
    riverFloodingType: state => state.riverFloodingType,
    dosyaInfo: state => state.dosyaInfo,
    snowInfo: state => state.snowInfo,
    usageGuide: state => state.usageGuide,
    hazardMapUsageGuide: state => state.hazardMapUsageGuide,
    alerts: state => state.alerts,
    isShowMobileHazardMap: state => state.isShowMobileHazardMap,
    isNavigationVisible: state => state.isNavigationVisible,
    isRightNavVisible: state => state.isRightNavVisible,
    isMonitoring: state => state.isMonitoring,
    isManholeMonitor: state => state.isManholeMonitor,
    isCameraMonitor: state => state.isCameraMonitor,
    kouzuiInfo: state => state.kouzuiInfo,
    isLoading: state => state.isLoading,
    wind: state => state.wind,
    selectedWind: state => state.selectedWind,
    isMobileObservatoryRisk: state => state.isMobileObservatoryRisk,
    manholeChartTopImage: state => state.manholeChartTopImage,
    overflowWaterwayChartTopImage: state => state.overflowWaterwayChartTopImage,
    suijinChartImage: state => state.suijinChartImage
  },
  mutations: {
    SET_MOBILE_HAZARD_MAP: function(state, payload) {
      state.mobileHazardMap = payload;
    },
    SET_BASE_DATE: function(state, payload) {
      state.baseDate = payload;
    },
    SET_SELECTED_RISK: function(state, payload) {
      state.selectedRisk = payload;
    },
    SET_SELECTED_FEATURES: function(state, payload) {
      state.selectedFeatures = payload;
    },
    SET_SELECTED_BASE_TILE: function(state, payload) {
      state.selectedBaseTile = payload;
    },
    SET_LEAFLET_MAP: function(state, payload) {
      state.leafletMap = payload;
    },
    SET_ALERTS: function(state, payload) {
      state.alerts = payload;
    },
    SET_OBSERVATORIES: function(state, payload) {
      state.observatories = payload;
    },
    SET_SELECTED_OBSERVATORY: function(state, payload) {
      state.selectedObservatory = payload;
    },
    SET_SELECTED_DATE: function(state, payload) {
      state.selectedDate = payload;
    },
    SET_RAIN_IMAGE_COORDINATE: function(state, payload) {
      state.rainImageCoordinate = payload;
    },
    SET_RAIN_IMAGE_INFO: function(state, payload) {
      state.rainImageInfo = payload;
    },
    SET_IMAGE_SIZE_INFO: function(state, payload) {
      state.imageSizeInfo = payload;
    },
    SET_RISK_BASE_DATE: function(state, payload) {
      state.riskBaseDate = payload;
    },
    SET_MAP_DETAIL: function(state, payload) {
      state.isShowMapDetail = payload.isShowing;
      state.mapDetail.title = payload.title;
      state.mapDetail.contents = payload.contents;
    },
    SET_RISK_SUB_CONTROL(state, payload) {
      const cloneRiskSubControl = Object.assign({}, state.riskSubControl);
      if (payload.observatory !== undefined) {
        cloneRiskSubControl.observatory = payload.observatory;
      }
      if (payload.opacity !== undefined) {
        cloneRiskSubControl.opacity = payload.opacity;
      }
      state.riskSubControl = cloneRiskSubControl;
    },
    ["SET_IS_SHOW_USAGE_GUIDE"](state, payload) {
      state.isShowUsageGuide = payload;
    },
    ["SET_SELECTED_AROUND_OBSERVATORIES"](state, payload) {
      state.selectedAroundObservatories = payload.observatories;
    },
    ["SET_RIVER_FLOODING_INFO"](state, payload) {
      state.riverFloodingInfo = payload;
    },
    SET_RIVER_FLOODING_TYPE: function(state, payload) {
      state.riverFloodingType = payload;
    },
    SET_DOSYA_INFO: function(state, payload) {
      state.dosyaInfo = payload;
    },
    SET_SNOW_INFO: function(state, payload) {
      state.snowInfo = payload;
    },
    SET_COGNITO_USER_INFO: function(state, payload) {
      state.user = payload;
    },
    SET_USAGE_GUIDE(state, payload) {
      state.usageGuide = payload;
    },
    SET_HAZARD_MAP_USAGE_GUIDE: function(state, payload) {
      state.hazardMapUsageGuide = payload;
    },
    SET_IS_SHOW_MOBILE_HAZARD_MAP: function(state, payload) {
      state.isShowMobileHazardMap = payload;
    },
    SET_IS_NAVIGATION: function(state, payload) {
      state.isNavigationVisible = payload;
    },
    SET_IS_RIGHT_NAV: function(state, payload) {
      state.isRightNavVisible = payload;
    },
    SET_IS_MONITORING: function(state, payload) {
      state.isMonitoring = payload;
    },
    SET_IS_MANHOLE_MONITOR: function(state, payload) {
      state.isManholeMonitor = payload;
    },
    SET_IS_CAMERA_MONITOR: function(state, payload) {
      state.isCameraMonitor = payload;
    },
    SET_KOUZUI_INFO: function(state, payload) {
      state.kouzuiInfo = payload;
    },
    SET_IS_LOADING: function(state, payload) {
      state.isLoading = payload;
    },
    SET_WIND_TIME_SERIES: function(state, payload) {
      state.wind.timeSeries = payload;
    },
    SET_WIND_DATA: function(state, payload) {
      state.wind.data = payload;
    },
    SET_SELECTED_WIND: function(state, payload) {
      state.selectedWind = payload;
    },
    SET_IS_MOBILE_OBSERVATORY_RISK: function(state, payload) {
      state.isMobileObservatoryRisk = payload;
    },
    SET_MANHOLE_CHART_TOP_IMAGE: function(state, payload) {
      state.manholeChartTopImage = payload;
    },
    SET_OVERFLOW_WATERWAY_CHART_TOP_IMAGE: function(state, payload) {
      state.overflowWaterwayChartTopImage = payload;
    },
    SET_SUIJIN_CHART_IMAGE_CAR_TATE: function(state, payload) {
      state.suijinChartImage.carTate = payload;
    },
    SET_SUIJIN_CHART_IMAGE_CAR_YOKO: function(state, payload) {
      state.suijinChartImage.carYoko = payload;
    },
    SET_SUIJIN_CHART_IMAGE_POLE: function(state, payload) {
      state.suijinChartImage.pole = payload;
    }
  },
  actions: {
    FETCH_OBSERVATORIES: async function(context) {
      let observatories = [];
      if (this.state.observatories.length === 0) {
        const frontConfig = await s3.getFrontConfig();
        const manholeStatuses = frontConfig.statuses;
        const res = await axiosHelper.get("/observatories", {
          domain: DOMAIN
        });
        if (res === null || !res.observatories) {
          context.commit("SET_OBSERVATORIES", []);
          return;
        }
        observatories = res.observatories;
        observatories.forEach(obs => {
          if (
            obs.type === OBSERVATORY.CAMERA.code ||
            obs.type === OBSERVATORY.LIVECAMERA.code ||
            obs.type === OBSERVATORY.DAM.code ||
            obs.type === OBSERVATORY.OVERFLOW_WATERWAY.code
          ) {
            obs.status = "normal";
          } else {
            obs.status = "deficit";
          }
          if (
            obs.type === OBSERVATORY.MANHOLE.code ||
            obs.type === OBSERVATORY.MANHOLE_WATERWAY.code
          ) {
            if (manholeStatuses[obs.id] !== undefined) {
              const alias = common.isPrivateMode ? "private" : "public";
              obs.is_mentenance =
                manholeStatuses[obs.id][alias] === "maintenance";
              obs.invisible = manholeStatuses[obs.id][alias] === "invisible";
            }
          }
        });
        observatories = observatories.filter(obs => obs.invisible !== true);
      } else {
        observatories = [...this.state.observatories];
      }

      const baseDate = this.state.baseDate.clone();

      const params = {
        domain: DOMAIN,
        date: baseDate.format(DATE_FORMAT)
      };
      const response = await axiosHelper.get(
        "/observatories/status/v2",
        params
      );
      if (response !== null) {
        const rains = response.rains;
        const stages = response.stages;
        const overflows = response.overflows ? response.overflows : {};
        const suijins = response.suijins;
        const pumps = response.pumps ? response.pumps : {};
        const manholes = response.manholes ? response.manholes : {};
        observatories.forEach(obs => {
          const obsId = obs.id;
          if (obs.type === OBSERVATORY.RAIN.code) {
            obs.status = "deficit";
            delete obs.min10;
            delete obs.total;
            if (rains[obsId] !== undefined) {
              if (rains[obsId].min10 !== undefined) {
                const min10 = rains[obsId].min10;
                obs.min10 = min10;
                if (min10 >= 20) {
                  obs.status = "4";
                } else if (min10 >= 10) {
                  obs.status = "3";
                } else if (min10 >= 5) {
                  obs.status = "2";
                } else if (min10 >= 1) {
                  obs.status = "1";
                } else if (min10 >= 0) {
                  obs.status = "0";
                }
              }
              if (rains[obsId].total !== undefined) {
                obs.total = rains[obsId].total;
              }
            }
          } else if (obs.type === OBSERVATORY.STAGE.code) {
            obs.status = "deficit";
            delete obs.level;
            delete obs.chartWaterBoxLevel;
            if (
              stages[obsId] !== undefined &&
              stages[obsId].level !== undefined
            ) {
              const level = stages[obsId].level;
              const date = stages[obsId].date;
              const targetDate = baseDate
                .clone()
                .subtract(30, "minutes")
                .format(DATE_FORMAT);
              if (obs.kiki === true && date < targetDate) {
                obs.chartWaterBoxLevel = level;
              } else {
                obs.level = level;
                if (
                  obs.outbreakLevel !== undefined &&
                  level >= obs.outbreakLevel
                ) {
                  obs.status = "outbreak";
                } else if (
                  obs.dangerousLevel !== undefined &&
                  level >= obs.dangerousLevel
                ) {
                  obs.status = "dangerous";
                } else if (
                  obs.evacuationLevel !== undefined &&
                  level >= obs.evacuationLevel
                ) {
                  obs.status = "evacuation";
                } else if (
                  obs.warningLevel !== undefined &&
                  level >= obs.warningLevel
                ) {
                  obs.status = "warning";
                } else if (
                  obs.standbyLevel !== undefined &&
                  level >= obs.standbyLevel
                ) {
                  obs.status = "standby";
                } else {
                  obs.status = "normal";
                }
              }
            }
          } else if (obs.type === OBSERVATORY.OVERFLOW_WATERWAY.code) {
            obs.status = "deficit";
            delete obs.level;
            if (
              overflows[obsId] !== undefined &&
              overflows[obsId].level !== undefined
            ) {
              const level = overflows[obsId].level;
              obs.level = level;
              if (
                obs.dangerousLevel !== undefined &&
                level >= obs.dangerousLevel
              ) {
                obs.status = "dangerous";
              } else if (
                obs.warningLevel !== undefined &&
                level >= obs.warningLevel
              ) {
                obs.status = "warning";
              } else {
                obs.status = "normal";
              }
            }
          } else if (obs.type === OBSERVATORY.PUMP.code) {
            obs.status = "deficit";
            if (pumps[obsId] !== undefined) {
              if (pumps[obsId].status !== undefined) {
                obs.status = pumps[obsId].status;
              }
              if (pumps[obsId].pump_status !== undefined) {
                const pumpStatus = pumps[obsId].pump_status.map((pump, i) => {
                  const text = common.getPumpStatusText(pump);
                  return `No${i + 1} ${text}`;
                });
                obs.tooltip = pumpStatus.join(", ");
              }
            }
          } else if (
            obs.type === OBSERVATORY.MANHOLE.code ||
            obs.type === OBSERVATORY.MANHOLE_WATERWAY.code
          ) {
            obs.status = "deficit";
            if (obs.is_mentenance !== true && manholes[obsId] !== undefined) {
              if (manholes[obsId].level !== undefined) {
                const level = manholes[obsId].level;
                obs.level = level;
                if (
                  obs.dangerousLevel !== undefined &&
                  level >= obs.dangerousLevel
                ) {
                  obs.status = "dangerous";
                } else if (
                  obs.warningLevel !== undefined &&
                  level >= obs.warningLevel
                ) {
                  obs.status = "warning";
                } else {
                  obs.status = "normal";
                }
              }
            }
          } else if (obs.type === OBSERVATORY.SUIJIN.code) {
            obs.status = "maintenace";
            if (suijins[obsId] !== undefined) {
              if (suijins[obsId].status !== undefined) {
                obs.status = suijins[obsId].status;
                if (suijins[obsId].isTopFlooded === true) {
                  obs.status = "topFlooded";
                }
              }
              if (suijins[obsId].level !== undefined) {
                obs.level = suijins[obsId].level;
              }
            }
          }
        });
      }

      // マンホールの予測値
      if (common.isPrivateMode) {
        const manholeForecasts = await axiosHelper.get(
          "/kashiwa-manhole-forecast",
          {
            date: baseDate.format(DATE_FORMAT)
          }
        );
        if (manholeForecasts !== null) {
          const manholes = observatories.filter(
            obs =>
              obs.type === OBSERVATORY.MANHOLE.code ||
              obs.type === OBSERVATORY.MANHOLE_WATERWAY.code
          );
          manholes.forEach(obs => {
            obs.forecastStatus = "";
            const obsLevel = obs.level;
            const timeSeries = manholeForecasts[obs.id];
            if (timeSeries === undefined || obsLevel === undefined) return;
            timeSeries.forEach(data => {
              const forecastLevel = data.value;
              if (
                obs.dangerousLevel !== undefined &&
                forecastLevel >= obs.dangerousLevel
              ) {
                obs.forecastStatus = "dangerous";
                return;
              } else if (
                obs.warningLevel !== undefined &&
                forecastLevel >= obs.warningLevel
              ) {
                obs.forecastStatus = "warning";
              }
            });
          });
        }
      }

      context.commit("SET_OBSERVATORIES", observatories);
    },
    FETCH_SELECTED_OBSERVATORY_DATA: async function(context, target) {
      const observatory = Object.assign({}, target);
      const baseDate = this.state.baseDate.clone();

      const params = {
        date: baseDate.format(DATE_FORMAT),
        id: observatory.id,
        type: observatory.type,
        rain_forecast: observatory.type === 1
      };
      if (observatory.type === 1) {
        params.hours = "24";
      }
      if (observatory.type === OBSERVATORY.OVERFLOW_WATERWAY.code) {
        params.hours = "25";
      }

      if (observatory.dynamodb) {
        params["dynamodb"] = observatory.dynamodb;
        if (observatory.type === 1) {
          params.hours = "24";
          params.lat = observatory.lat;
          params.lng = observatory.lng;
        }
      }
      if (observatory.bucket) {
        params["bucket"] = observatory.bucket;
      }
      if (observatory.prefix) {
        params["prefix"] = observatory.prefix;
      }

      const response = await axiosHelper.get("/observatories/data", params);
      if (response === null) {
        throw new Error("fetch failed : observatories/data");
      }

      const selected = Object.assign({}, target);
      selected.baseDate = response.baseDate;
      selected.timeSeries = response.timeSeries;

      // マンホール、水路は上限値を設定
      common.convertManholeTimeSeries(selected);

      if (
        common.isPublicMode &&
        [OBSERVATORY.MANHOLE.code, OBSERVATORY.MANHOLE_WATERWAY.code].includes(
          selected.type
        )
      ) {
        selected.timeSeries = selected.timeSeries.filter(
          data => data.type === "real"
        );
      }

      let contents = "";
      let title = "";
      const observatoryName = selected.name === undefined ? "" : selected.name;
      switch (selected.type) {
        case OBSERVATORY.RAIN.code:
          contents = "rainfall-detail";
          title = `${OBSERVATORY.RAIN.name}観測所 - ${observatoryName}`;
          break;
        case OBSERVATORY.STAGE.code:
          contents = "stage-detail";
          title = `${OBSERVATORY.STAGE.name}観測所 - ${observatoryName}`;
          break;
        case OBSERVATORY.DAM.code:
          contents = "dam-detail";
          title = `${OBSERVATORY.DAM.name}観測所 - ${observatoryName}`;
          break;
        case OBSERVATORY.CAMERA.code:
          contents = "camera-detail";
          title = `${OBSERVATORY.CAMERA.name}`;
          break;
        case OBSERVATORY.LIVECAMERA.code:
          contents = "live-camera-detail";
          title = `${OBSERVATORY.LIVECAMERA.name}`;
          break;
        case OBSERVATORY.OVERFLOW_WATERWAY.code:
          contents = "stage-detail";
          title = `${OBSERVATORY.OVERFLOW_WATERWAY.name} - ${observatoryName}`;
          break;
        case OBSERVATORY.SUIJIN.code:
          contents = "suijin-detail";
          title = `浸水センサ - ${observatoryName}`;
          break;
        case OBSERVATORY.MANHOLE.code:
          contents = "manhole-detail";
          title = `${OBSERVATORY.MANHOLE.name} - ${observatoryName}`;
          break;
        case OBSERVATORY.MANHOLE_WATERWAY.code:
          contents = "waterway-detail";
          title = `${OBSERVATORY.MANHOLE_WATERWAY.name} - ${observatoryName}`;
          break;
        case OBSERVATORY.PUMP.code:
          contents = "pump-detail";
          title = `${OBSERVATORY.PUMP.name} - ${observatoryName}`;
          break;
      }
      context.commit("SET_SELECTED_OBSERVATORY", selected);

      context.commit("SET_MAP_DETAIL", {
        isShowing: true,
        title: title,
        contents: contents
      });
    },
    FETCH_RIVER_FLOODING: async function(context) {
      const baseDate = this.state.baseDate.clone();
      baseDate.subtract(baseDate.minute() % 5, "minutes");

      const parameters = {
        date: baseDate.utc().format(DATE_FORMAT)
      };

      const response = await axiosHelper.get("/river-floods", parameters);
      if (response === null) {
        throw new Error("fetch failed : river-floods");
      }

      context.commit(
        "SET_RISK_BASE_DATE",
        moment.utc(response.baseDate, DATE_FORMAT)
      );
      const areas = response.areas.map(area => area);
      context.commit("SET_RIVER_FLOODING_INFO", areas);

      context.commit(
        "SET_SELECTED_DATE",
        moment.utc(response.baseDate, DATE_FORMAT)
      );
    },
    FETCH_DOSYA: async function(context) {
      const baseDate = this.state.baseDate.clone();
      baseDate.subtract(baseDate.minute() % 10, "minutes");

      const params = {
        date: baseDate.utc().format(DATE_FORMAT)
      };
      const res = await axiosHelper.get("/dosya", params);
      if (res === null) {
        context.commit("SET_DOSYA_INFO", {});
        return;
      }
      const dosyaInfo = {
        bounds: [],
        imageInfos: [],
        size: {}
      };
      if (!res.imageInfos) {
        context.commit("SET_DOSYA_INFO", dosyaInfo);
        return;
      }

      dosyaInfo.bounds = res.bounds;

      const loadImage = src => {
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = e => reject(e);
          image.src = src;
        });
      };

      const infos = res.imageInfos.map(async info => {
        const image = await loadImage(info.url);
        return {
          date: info.date,
          image: image,
          url: info.url
        };
      });
      const infosRes = await Promise.all(infos);
      dosyaInfo.images = infosRes.filter(row => row.image !== null);
      context.commit("SET_DOSYA_INFO", dosyaInfo);

      context.commit(
        "SET_RISK_BASE_DATE",
        moment.utc(res.baseDate, DATE_FORMAT)
      );

      context.commit(
        "SET_SELECTED_DATE",
        res.baseDate ? moment.utc(res.baseDate, DATE_FORMAT) : baseDate
      );
    }
  }
});
